import {
  collection,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { CallLogEntry } from './CalendarTypes';

export class CallLogService {
  static async fetchCallLogEntries(
    companyId: string,
    selectedDate: string,
    selectedUserId: string
  ): Promise<CallLogEntry[]> {
    if (!companyId || !selectedDate || !selectedUserId) return [];

    try {
      const callLogsRef = collection(db, `companies/${companyId}/callLogs`);
      const selectedDateStart = new Date(selectedDate);
      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);
      
      // Filter call logs by selected user and date range
      const callLogsQuery = query(
        callLogsRef,
        where('userId', '==', selectedUserId),
        where('callTimestamp', '>=', Timestamp.fromDate(selectedDateStart)),
        where('callTimestamp', '<=', Timestamp.fromDate(selectedDateEnd)),
        orderBy('callTimestamp', 'desc')
      );

      const callLogsSnapshot = await getDocs(callLogsQuery);
      const callLogsData: CallLogEntry[] = [];

      for (const callLogDoc of callLogsSnapshot.docs) {
        const callLogData = callLogDoc.data();
        
        // Fetch lead name if leadUid exists
        let leadName = 'Unknown Lead';
        if (callLogData.leadUid) {
          try {
            const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads`, callLogData.leadUid));
            if (leadDoc.exists()) {
              const leadData = leadDoc.data();
              leadName = leadData.name || leadData.studentName || 'Unknown Lead';
            }
          } catch (error) {
            console.error('Error fetching lead name:', error);
          }
        }

        // Fetch user name
        let userName = 'Unknown User';
        try {
          const userDoc = await getDoc(doc(db, 'users', callLogData.userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userName = userData.name || userData.email || 'Unknown User';
          }
        } catch (error) {
          console.error('Error fetching user name:', error);
        }

        callLogsData.push({
          id: callLogDoc.id,
          userId: callLogData.userId,
          userName,
          leadUid: callLogData.leadUid || '',
          leadName,
          type: callLogData.type || 'OUTGOING',
          duration: callLogData.duration || 0,
          callTimestamp: callLogData.callTimestamp,
          entryType: 'callLog',
          isDone: undefined
        });
      }

      return callLogsData;
    } catch (error) {
      console.error('Error fetching call log entries:', error);
      return [];
    }
  }

  // Helper method to format call duration
  static formatDuration(duration: number): string {
    if (duration === 0) return '0s';
    
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60); // Round down to remove decimals
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Helper method to get call type display
  static getCallTypeDisplay(type: string): { label: string; color: string } {
    switch (type) {
      case 'OUTGOING':
        return { label: 'Outgoing', color: 'text-blue-600 bg-blue-50' };
      case 'INCOMING':
        return { label: 'Incoming', color: 'text-green-600 bg-green-50' };
      case 'MISSED':
        return { label: 'Missed', color: 'text-red-600 bg-red-50' };
      case 'UNKNOWN':
        return { label: 'Unknown', color: 'text-gray-600 bg-gray-50' };
      default:
        return { label: 'Call', color: 'text-gray-600 bg-gray-50' };
    }
  }
}
